import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/Seo/SeoHead";
import TrialProductModal from "../../components/TrialProductModal/TrialProductModal";
import {
  TbCalendar,
  TbMoon,
  TbBell,
  TbRobot,
  TbChartBar,
  TbRefresh,
  TbMessageCircle,
  TbBrandWhatsapp,
  TbBrandInstagram,
  TbBuildingBank,
  TbCurrencyReal,
  TbHeadset,
  TbAddressBook,
  TbSpeakerphone,
  TbUsers,
  TbGift,
  TbPlugConnected,
} from "react-icons/tb";
import styles from "./RecursosPage.module.css";
import {
  BUSINESS_SEGMENTS,
  CTA_PAGES,
  CTA_PLACEMENTS,
  DESTINATIONS,
  trackTrialCtaClick,
} from "../../analytics";
import {
  TRIAL_PRODUCTS,
  buildTrialSignupPath,
} from "../../constants/trialProduct";

/**
 * Recursos — página institucional dos DOIS produtos.
 *
 * Até 18/08/2026 esta página descrevia só o Agendamento: falava de agenda,
 * lembrete e "delegar o agendamento para a IA". Quem chegasse aqui vindo de
 * `/atendimento` leria uma lista de recursos que o seu tier não tem, e assinaria
 * esperando agenda.
 *
 * A separação em dois blocos não é diagramação — é o contrato de cada plano.
 * `SHARED` é o que `schedulingIncluded: false` mantém; `SCHEDULING_ONLY` é
 * exatamente o que aquele campo desliga no backend. Mover um item de bloco aqui
 * sem mover o gate lá é como a página volta a mentir.
 */

/** O que os DOIS produtos entregam. */
const SHARED_FEATURES = [
  {
    Icon: TbRobot,
    title: "IA que entende linguagem natural",
    text: "O cliente não precisa digitar um formulário nem escolher opção de menu. Manda uma mensagem normal e o bot entende o que ele quer.",
  },
  {
    Icon: TbMoon,
    title: "Responde às 23h de domingo",
    text: "Sem depender de você estar online. O bot atende 24/7 no WhatsApp e no Direct do Instagram.",
  },
  {
    Icon: TbMessageCircle,
    title: "Responde as dúvidas que você cadastrar",
    text: "Horário, endereço, formas de pagamento, estacionamento — você escreve pergunta e resposta em português, e ele passa a responder.",
  },
  {
    Icon: TbCurrencyReal,
    title: "Informa preço na hora",
    text: "O cliente pergunta quanto custa e recebe o valor do serviço que você cadastrou, sem esperar alguém abrir a tabela.",
  },
  {
    Icon: TbHeadset,
    title: "Passa para uma pessoa quando não é caso de bot",
    text: "Reclamação, cancelamento de plano, pedido que ele não entende: o bot reconhece, diz isso ao cliente e encaminha a conversa para a sua equipe — sem inventar resposta.",
  },
  {
    Icon: TbAddressBook,
    title: "Cada conversa vira um contato",
    text: "Quem chamou fica registrado no painel, com o que perguntou. Mesmo quem não fechou nada na hora — é a sua lista para chamar de volta.",
  },
  {
    Icon: TbSpeakerphone,
    title: "Promoções que expiram sozinhas",
    text: "Cadastre a campanha com data de validade. Passou a data, o bot para de mencionar — você não precisa lembrar de apagar.",
  },
  {
    Icon: TbMessageCircle,
    title: "Histórico do cliente preservado",
    text: 'O bot lembra o que o cliente pediu na última vez. "Da última vez foi degradê na régua, quer o mesmo?" — fidelização natural.',
  },
];

/**
 * O que SÓ o Chatbot de Agendamento entrega.
 *
 * Espelho do que `schedulingIncluded: false` desliga no backend: os 6 intents de
 * agenda no classificador, as rotas de escrita da agenda e o relatório semanal
 * (o `weeklyReportJob` filtra por `plan.is.schedulingIncluded: true`).
 */
const SCHEDULING_FEATURES = [
  {
    Icon: TbCalendar,
    title: "Agenda que se organiza sozinha",
    text: "O bot escreve na sua agenda e lê dela antes de confirmar. Sem duplo agendamento, sem horário marcado em cima de outro.",
  },
  {
    Icon: TbBell,
    title: "Lembretes automáticos que reduzem faltas",
    text: "Aviso automático antes do horário — quem esqueceu recebe a tempo de confirmar ou remarcar.",
  },
  {
    Icon: TbUsers,
    title: "Cliente escolhe o profissional",
    text: "Ele pede pelo nome e o bot confere aquela agenda. Se estiver ocupado, oferece os horários livres e diz quem mais está disponível na hora pedida.",
  },
  {
    Icon: TbRefresh,
    title: "Lista de espera inteligente",
    text: "Quando um horário cancela, o próximo da fila é avisado automaticamente — sem você precisar fazer nada.",
  },
  {
    Icon: TbGift,
    title: "Programa de fidelidade",
    text: "O cliente acumula atendimentos e o bot avisa quando o cartão fecha. Ele mesmo responde quantos faltam, sem você conferir.",
  },
  {
    Icon: TbChartBar,
    title: "Relatório semanal automático",
    text: "Todo fim de semana chega no seu WhatsApp um resumo de agendamentos, receita e comparecimento.",
  },
];

/**
 * Integrações.
 *
 * ⚠️ Duas ausências deliberadas, ambas de 18/08/2026:
 *
 * - **Google Calendar** — a integração NÃO EXISTE, e a decisão de não a ter está
 *   registrada na ADR-0028. O `services/googleCalendarService.js` que restava do
 *   lado do cliente foi APAGADO em 18/08/2026: era órfão, chamava quatro rotas
 *   (`/auth/google/url|status|disconnect|events`) que nunca existiram no backend,
 *   e três delas com `/api` duplicado. Enquanto o arquivo existia, ele parecia
 *   uma integração "só faltando ligar" — e foi por isso que a promessa voltou
 *   duas vezes à vitrine.
 * - **"Pagamento no agendamento"** — não existe cobrança do cliente final. Não há
 *   model `Payment`, `Appointment` não tem campo de valor, e o `asaasService`
 *   declara no próprio cabeçalho que faz "assinaturas recorrentes utilizadas no
 *   fluxo de billing da plataforma". O Asaas cobra VOCÊ pela Nexus; ele não cobra
 *   o seu cliente. O rótulo abaixo diz isso.
 *
 * Para reintroduzir qualquer um dos dois: implemente primeiro, anuncie depois.
 */
const INTEGRATIONS = [
  { Icon: TbBrandWhatsapp, label: "WhatsApp Business" },
  { Icon: TbBrandInstagram, label: "Instagram Direct" },
  { Icon: TbPlugConnected, label: "NextFit (academias)" },
  { Icon: TbBuildingBank, label: "Asaas — cobrança da sua assinatura" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Recursos da Nexus Chatbot — Chatbot de Atendimento e de Agendamento",
  description:
    "Tudo que os chatbots da Nexus fazem: atendimento 24/7 no WhatsApp e Instagram, respostas e preços automáticos, captação de contatos — e, no plano de Agendamento, agenda, lembretes e lista de espera.",
  provider: { "@type": "Organization", name: "Nexus Chatbot" },
  url: "https://nexustech.tec.br/recursos",
};

/** Contexto fixo desta página para o funil de teste grátis (GA4). */
const CTA_CTX = {
  cta_page: CTA_PAGES.RECURSOS,
  business_segment: BUSINESS_SEGMENTS.GENERAL,
};

/** Destinos diretos dos CTAs finais, um por produto. */
const SIGNUP_AGENDAMENTO = buildTrialSignupPath(TRIAL_PRODUCTS.AGENDAMENTO);
const SIGNUP_ATENDIMENTO = buildTrialSignupPath(TRIAL_PRODUCTS.ATENDIMENTO);

const FeatureGrid = ({ features }) => (
  <ul className={styles.grid}>
    {features.map((feature) => (
      <li key={feature.title} className={styles.card}>
        <feature.Icon className={styles.cardIcon} size={36} aria-hidden="true" />
        <h3 className={styles.cardTitle}>{feature.title}</h3>
        <p className={styles.cardText}>{feature.text}</p>
      </li>
    ))}
  </ul>
);

const RecursosPage = () => {
  // O CTA do hero abre o modal, como na home: esta página apresenta os DOIS
  // produtos, então mandar o clique direto ao cadastro faria o servidor escolher
  // por quem acabou de ler a comparação. Os CTAs do fim são diretos — quem
  // chegou até lá já leu a diferença e decidiu.
  const [trialPlacement, setTrialPlacement] = useState(null);
  const openTrialModal = useCallback(
    (placement) => setTrialPlacement(placement),
    [],
  );
  const closeTrialModal = useCallback(() => setTrialPlacement(null), []);

  const trackFinalCta = useCallback(
    (product) =>
      trackTrialCtaClick({
        ...CTA_CTX,
        cta_placement: CTA_PLACEMENTS.FINAL,
        destination: DESTINATIONS.LOGIN_SIGNUP,
        product,
      }),
    [],
  );

  return (
    <>
      <SeoHead
        title="Recursos | Nexus Chatbot — Atendimento e Agendamento por WhatsApp"
        description="Atendimento 24/7 no WhatsApp e Instagram, respostas e preços automáticos e captação de contatos nos dois planos. No de Agendamento, também agenda, lembretes e lista de espera."
        canonical="https://nexustech.tec.br/recursos"
        jsonLd={JSON_LD}
      />

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <span className={styles.heroBadge} aria-hidden="true">
            Tudo que você precisa
          </span>
          <h1 className={styles.heroTitle}>
            Recursos que trabalham <em>enquanto você atende</em>
          </h1>
          <p className={styles.heroSubtitle}>
            São dois chatbots. Os dois respondem seu cliente 24 horas pelo
            WhatsApp e pelo Instagram; o de Agendamento ainda cuida da agenda.
            Abaixo, o que cada um faz.
          </p>
          <button
            type="button"
            className={styles.heroCta}
            onClick={() => openTrialModal(CTA_PLACEMENTS.HERO)}
          >
            Testar grátis por 14 dias
          </button>
          <p className={styles.heroNote}>Sem cartão. Cancele quando quiser.</p>
        </div>
      </section>

      {/* ── BASE COMUM AOS DOIS ── */}
      <section className={styles.features} aria-labelledby="features-heading">
        <div className={styles.featuresInner}>
          <h2 id="features-heading" className={styles.sectionTitle}>
            Nos dois chatbots
          </h2>
          <p className={styles.sectionSubtitle}>
            A base que todo negócio recebe, independente do plano — inclusive
            quem não trabalha com hora marcada.
          </p>

          <FeatureGrid features={SHARED_FEATURES} />
        </div>
      </section>

      {/* ── EXCLUSIVO DO AGENDAMENTO ── */}
      <section
        className={styles.featuresAlt}
        aria-labelledby="scheduling-heading"
      >
        <div className={styles.featuresInner}>
          <span className={styles.groupBadge}>
            Só no Chatbot de Agendamento
          </span>
          <h2 id="scheduling-heading" className={styles.sectionTitle}>
            Quando o seu negócio é de hora marcada
          </h2>
          <p className={styles.sectionSubtitle}>
            Tudo da lista acima, mais a agenda. É o que separa os dois planos —
            pensado para barbearias, consultórios, mecânicas, lava-jatos e
            academias.
          </p>

          <FeatureGrid features={SCHEDULING_FEATURES} />

          <p className={styles.groupNote}>
            Não trabalha com hora marcada?{" "}
            <Link to="/atendimento" className={styles.groupNoteLink}>
              O Chatbot de Atendimento
            </Link>{" "}
            entrega a lista de cima e custa menos.
          </p>
        </div>
      </section>

      {/* ── INTEGRAÇÕES ── */}
      <section
        className={styles.integrations}
        aria-labelledby="integrations-heading"
      >
        <h2 id="integrations-heading" className={styles.integrationsTitle}>
          Integra com as ferramentas que você já usa
        </h2>
        <ul className={styles.integrationsGrid}>
          {INTEGRATIONS.map((item) => (
            <li key={item.label} className={styles.integrationBadge}>
              <item.Icon
                className={styles.integrationIcon}
                size={18}
                aria-hidden="true"
              />
              {item.label}
            </li>
          ))}
        </ul>
      </section>

      {/* ── CTA FINAL: um por produto ── */}
      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Qual deles é o seu?</h2>
          <p className={styles.ctaSubtitle}>
            14 dias grátis nos dois, sem cartão. Configure em menos de 15
            minutos e comece a atender ainda hoje.
          </p>
          <div className={styles.ctaButtons}>
            <Link
              to={SIGNUP_AGENDAMENTO}
              className={styles.ctaButton}
              onClick={() => trackFinalCta(TRIAL_PRODUCTS.AGENDAMENTO)}
            >
              Testar o de Agendamento
            </Link>
            <Link
              to={SIGNUP_ATENDIMENTO}
              className={styles.ctaButtonSecondary}
              onClick={() => trackFinalCta(TRIAL_PRODUCTS.ATENDIMENTO)}
            >
              Testar o de Atendimento
            </Link>
          </div>
        </div>
      </section>

      <TrialProductModal
        isOpen={trialPlacement !== null}
        onClose={closeTrialModal}
        ctaPlacement={trialPlacement}
      />
    </>
  );
};

export default RecursosPage;
